(() => {
  'use strict';

  const INITIALIZED_FLAG = 'wellnessJourneyTimelineInitialized';
  const INITIALIZER = 'initializeWellnessJourneyTimelines';

  if (window[INITIALIZED_FLAG]) {
    if (typeof window[INITIALIZER] === 'function') window[INITIALIZER](document);
    return;
  }
  window[INITIALIZED_FLAG] = true;

  const TIMELINE_SELECTOR = '[data-wellness-journey-richtext]';
  const STAGE_SELECTOR = '[data-wellness-journey-stage]';
  const CTA_SELECTOR = '[data-wellness-journey-cta]';
  const MAIN_PRODUCT_SELECTOR = 'product-info[id^="MainProduct-"]';
  const BUY_BOX_SELECTOR = '[id^="ProductInfo-"], .product__info-container';
  const PURCHASE_OPTIONS_SELECTOR = [
    '.loop-widget-purchase-options-container',
    '[data-loop-widget-purchase-options-container]',
    '[data-loop-widget-purchase-options]',
  ].join(',');
  const PURCHASE_OPTION_SELECTOR = '.loop-widget-purchase-option';
  const SELECTED_OPTION_CLASS = 'loop-widget-purchase-option-selected';
  const ONE_TIME_ATTRIBUTE = 'data-loop-widget-onetime-purchase-option';

  const buildRichTextStages = (timeline) => {
    if (!timeline.hasAttribute('data-wellness-journey-richtext')) return;

    const richText = timeline.querySelector('.metafield-rich_text_field');
    if (!richText || richText.dataset.wellnessJourneyRichtextBuilt === 'true') return;

    const sourceElements = Array.from(richText.children);
    const hasStageHeadings = sourceElements.some((element) => element.matches('h1, h2, h3, h4, h5, h6'));
    if (!hasStageHeadings) return;

    const fragment = document.createDocumentFragment();
    let currentStage = null;

    sourceElements.forEach((element) => {
      if (element.matches('h1, h2, h3, h4, h5, h6')) {
        currentStage = document.createElement('div');
        currentStage.className = 'wellness-journey__stage';
        currentStage.setAttribute('data-wellness-journey-stage', '');

        const node = document.createElement('span');
        node.className = 'wellness-journey__node';
        node.setAttribute('aria-hidden', 'true');

        element.className = 'wellness-journey__stage-title';
        currentStage.append(node, element);
        fragment.append(currentStage);
        return;
      }

      if (!currentStage) return;

      if (element.matches('ul, ol')) {
        element.className = 'wellness-journey__items';

        element.querySelectorAll(':scope > li').forEach((item) => {
          item.className = 'wellness-journey__item';

          const check = document.createElement('span');
          check.className = 'wellness-journey__check';
          check.setAttribute('aria-hidden', 'true');
          check.textContent = '✓';

          const content = document.createElement('span');
          content.className = 'wellness-journey__item-content';
          content.append(...Array.from(item.childNodes));

          item.replaceChildren(check, content);
        });
      }

      currentStage.append(element);
    });

    richText.replaceChildren(fragment);
    richText.classList.add('wellness-journey__richtext');
    richText.dataset.wellnessJourneyRichtextBuilt = 'true';
  };

  const initializeTimeline = (timeline) => {
    if (!(timeline instanceof Element) || timeline.dataset.wellnessJourneyInitialized === 'true') return;

    buildRichTextStages(timeline);

    const stages = Array.from(timeline.querySelectorAll(STAGE_SELECTOR));
    if (!stages.length) return;

    timeline.dataset.wellnessJourneyInitialized = 'true';

    if (!('IntersectionObserver' in window)) {
      stages.forEach((stage) => stage.classList.add('is-active'));
      return;
    }

    const activationOffset = Math.min(200, Math.round(window.innerHeight * 0.25));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.rootBounds) return;

          const clearedActivationLine = entry.boundingClientRect.bottom <= entry.rootBounds.bottom;
          const scrolledPastViewport = entry.boundingClientRect.top <= entry.rootBounds.top;
          entry.target.classList.toggle('is-active', clearedActivationLine || scrolledPastViewport);
        });
      },
      {
        rootMargin: `0px 0px -${activationOffset}px 0px`,
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    stages.forEach((stage) => observer.observe(stage));
    timeline.wellnessJourneyObserver = observer;
  };

  const initializeTimelines = (root = document) => {
    if (root instanceof Element && root.matches(TIMELINE_SELECTOR)) initializeTimeline(root);
    root.querySelectorAll?.(TIMELINE_SELECTOR).forEach(initializeTimeline);
  };

  const disconnectTimelines = (root) => {
    const timelines = [];

    if (root instanceof Element && root.matches(TIMELINE_SELECTOR)) timelines.push(root);
    root.querySelectorAll?.(TIMELINE_SELECTOR).forEach((timeline) => timelines.push(timeline));

    timelines.forEach((timeline) => {
      timeline.wellnessJourneyObserver?.disconnect();
      delete timeline.wellnessJourneyObserver;
      delete timeline.dataset.wellnessJourneyInitialized;
    });
  };

  window[INITIALIZER] = initializeTimelines;

  const isRendered = (element) => {
    if (!element || !element.isConnected) return false;

    const styles = window.getComputedStyle(element);
    if (styles.display === 'none' || styles.visibility === 'hidden') return false;

    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };

  const findMainProduct = () =>
    Array.from(document.querySelectorAll(MAIN_PRODUCT_SELECTOR)).find(isRendered) || null;

  const findPurchaseOptions = () => {
    const productRoots = Array.from(document.querySelectorAll(MAIN_PRODUCT_SELECTOR)).filter(isRendered);

    for (const productRoot of productRoots) {
      const purchaseOptions = Array.from(productRoot.querySelectorAll(PURCHASE_OPTIONS_SELECTOR)).find(isRendered);
      if (purchaseOptions) return purchaseOptions;
    }

    return Array.from(document.querySelectorAll(PURCHASE_OPTIONS_SELECTOR)).find(isRendered) || null;
  };

  const findBuyBox = () => {
    const productRoot = findMainProduct();
    if (!productRoot) return null;

    return Array.from(productRoot.querySelectorAll(BUY_BOX_SELECTOR)).find(isRendered) || productRoot;
  };

  const isUnavailable = (option) => {
    if (
      option.matches(
        '[disabled], [aria-disabled="true"], [data-disabled="true"], [data-loop-widget-disabled="true"], .disabled, .is-disabled'
      )
    ) {
      return true;
    }

    const control = option.querySelector('input, button, select');
    return Boolean(control && (control.disabled || control.getAttribute('aria-disabled') === 'true'));
  };

  const selectSubscription = (purchaseOptions) => {
    const selectedOption = purchaseOptions.querySelector(`.${SELECTED_OPTION_CLASS}`);

    if (selectedOption && !selectedOption.hasAttribute(ONE_TIME_ATTRIBUTE)) return false;

    const subscriptionOption = Array.from(
      purchaseOptions.querySelectorAll(PURCHASE_OPTION_SELECTOR)
    ).find(
      (option) =>
        isRendered(option) && !option.hasAttribute(ONE_TIME_ATTRIBUTE) && !isUnavailable(option)
    );

    if (!subscriptionOption) return false;

    subscriptionOption.click();
    return true;
  };

  const getFallbackTarget = (button) => {
    const selector = button.getAttribute('href')?.trim();
    if (!selector || selector === '#' || !selector.startsWith('#')) return null;

    try {
      return document.querySelector(selector);
    } catch (error) {
      return null;
    }
  };

  const getStickyOffset = () => {
    const stickySelectors = [
      '.shopify-section-header-sticky',
      '.section-header',
      '.announcement-bar-section',
      '.sticky-announcement-bar-section',
      '#shopify-section-announcement-bar',
    ];
    const stickyElements = new Set(document.querySelectorAll(stickySelectors.join(',')));
    let visibleBottom = 0;
    let reservedHeight = 0;

    document.querySelectorAll('sticky-header').forEach((element) => {
      if (!element.closest('.section-header')) stickyElements.add(element);
    });

    stickyElements.forEach((element) => {
      if (!isRendered(element)) return;

      const styles = window.getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      if (styles.position !== 'fixed' && styles.position !== 'sticky') return;

      reservedHeight += rect.height;

      if (rect.top <= 16 && rect.bottom > 0) {
        visibleBottom = Math.max(visibleBottom, rect.bottom);
      }
    });

    return Math.max(16, visibleBottom + 16, reservedHeight + 16);
  };

  const scrollToTarget = (target) => {
    if (!target || !target.isConnected) return;

    const top = window.scrollY + target.getBoundingClientRect().top - getStickyOffset();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({
      top: Math.max(0, top),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  };

  const handleCtaClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest(CTA_SELECTOR);
    if (!button) return;

    const purchaseOptions = findPurchaseOptions();
    const buyBox = findBuyBox();
    const fallbackTarget = buyBox || getFallbackTarget(button);
    if (!purchaseOptions && !fallbackTarget) return;

    event.preventDefault();

    const subscriptionChanged = purchaseOptions ? selectSubscription(purchaseOptions) : false;

    const finishScroll = () => {
      const currentPurchaseOptions = findPurchaseOptions();
      const currentBuyBox = findBuyBox();
      scrollToTarget(currentPurchaseOptions || purchaseOptions || currentBuyBox || fallbackTarget);
    };

    if (subscriptionChanged) {
      requestAnimationFrame(() => requestAnimationFrame(finishScroll));
    } else {
      finishScroll();
    }
  };

  document.addEventListener('click', handleCtaClick);
  document.addEventListener('shopify:section:load', (event) => initializeTimelines(event.target));
  document.addEventListener('shopify:section:unload', (event) => disconnectTimelines(event.target));
  initializeTimelines();
})();
